import { useState } from "react";
import { useOrg } from "@/lib/orgContext";
import { CountUp, HoverLift } from "@/components/motion";
import { useListMitigations, useCreateMitigation, useUpdateMitigation, useListCoaches } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Plus, CheckCircle2, Clock, ShieldAlert, Filter, Users } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { SampleBadge } from "@/components/SampleBadge";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-signal-red/10 text-signal-red border-signal-red/25",
  high: "bg-orange-500/10 text-orange-600 border-orange-400/30",
  medium: "bg-gold/10 text-gold border-gold/30",
  low: "bg-blue-500/10 text-blue-600 border-blue-400/30",
};

const STATUS_STYLES: Record<string, string> = {
  open: "bg-signal-red/8 text-signal-red border-signal-red/20",
  in_progress: "bg-blue-500/10 text-blue-600 border-blue-400/25",
  pending_review: "bg-purple-500/10 text-purple-600 border-purple-400/25",
  resolved: "bg-field-green/10 text-field-green border-field-green/25",
  dismissed: "bg-muted text-muted-foreground border-border",
};

const TYPE_LABELS: Record<string, string> = {
  contract_expiry: "Contract Expiry",
  insurance_expiry: "Insurance Expiry",
  insurance_missing: "Insurance Missing",
  buyout_risk: "Buyout Risk",
  compliance_violation: "Compliance Violation",
  performance_concern: "Performance Concern",
  legal_review: "Legal Review",
  other: "Other",
};

const defaultForm = {
  coachId: "",
  type: "other",
  title: "",
  description: "",
  severity: "medium",
  mitigationPlan: "",
  assignedTo: "",
  dueDate: "",
};

export default function Mitigations() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";
  const [statusFilter, setStatusFilter] = useState("active");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolveDialog, setResolveDialog] = useState<number | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [form, setForm] = useState({ ...defaultForm });

  const activeStatuses = ["open", "in_progress", "pending_review"];

  const { data: mitigations, isLoading } = useListMitigations(
    statusFilter === "active"
      ? {}
      : statusFilter === "resolved"
      ? { status: "resolved" as const }
      : { status: statusFilter as "open" | "in_progress" | "pending_review" | "resolved" | "dismissed" }
  );
  const { data: coaches } = useListCoaches();
  const createMitigation = useCreateMitigation();
  const updateMitigation = useUpdateMitigation();

  const filtered = mitigations?.filter((m) => {
    if (statusFilter === "active") {
      if (!activeStatuses.includes(m.status)) return false;
    }
    if (severityFilter !== "all" && m.severity !== severityFilter) return false;
    return true;
  }) ?? [];

  const openCount = mitigations?.filter((m) => activeStatuses.includes(m.status)).length ?? 0;
  const criticalCount = mitigations?.filter((m) => m.severity === "critical" && activeStatuses.includes(m.status)).length ?? 0;
  const resolvedCount = mitigations?.filter((m) => m.status === "resolved").length ?? 0;

  const handleCreate = () => {
    if (!form.title || !form.description || !form.type || !form.severity) {
      toast({ title: "Validation Error", description: "Title, description, type, and severity are required.", variant: "destructive" });
      return;
    }
    createMitigation.mutate({
      data: {
        coachId: form.coachId ? parseInt(form.coachId) : undefined,
        type: form.type as any,
        title: form.title,
        description: form.description,
        severity: form.severity as any,
        mitigationPlan: form.mitigationPlan || undefined,
        assignedTo: form.assignedTo || undefined,
        dueDate: form.dueDate || undefined,
      },
    }, {
      onSuccess: () => {
        toast({ title: "Risk Item Created", description: "Mitigation item has been logged." });
        queryClient.invalidateQueries({ queryKey: ["/api/mitigations"] });
        setDialogOpen(false);
        setForm({ ...defaultForm });
      },
      onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not create risk item" }),
    });
  };

  const handleStatusChange = (id: number, status: string) => {
    updateMitigation.mutate({ id, data: { status: status as any } }, {
      onSuccess: () => {
        toast({ title: "Status Updated" });
        queryClient.invalidateQueries({ queryKey: ["/api/mitigations"] });
      },
    });
  };

  const handleResolve = (id: number) => {
    updateMitigation.mutate({ id, data: { status: "resolved" as const, resolvedBy: "Admin User", resolutionNotes } }, {
      onSuccess: () => {
        toast({ title: "Item Resolved", description: "Risk item marked as resolved." });
        queryClient.invalidateQueries({ queryKey: ["/api/mitigations"] });
        setResolveDialog(null);
        setResolutionNotes("");
      },
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Risk & Mitigation Tracker</h1>
          <p>Track, assign, and resolve compliance and contract risks before they escalate.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />Log Risk Item
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger-children">
        <HoverLift>
          <Card className="animate-count-up card-lift kpi-card-gold h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Open Items</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-gold-subtle flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-gold" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value text-gold">
                {isLoading ? "—" : <CountUp value={openCount} />}
              </div>
              <p className="text-xs text-muted-foreground">Require action</p>
            </CardContent>
          </Card>
        </HoverLift>
        <HoverLift>
          <Card className="animate-count-up card-lift kpi-card-red h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Critical</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-signal-red-subtle flex items-center justify-center">
                <ShieldAlert className="h-4 w-4 text-signal-red" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value text-signal-red">
                {isLoading ? "—" : <CountUp value={criticalCount} />}
              </div>
              <p className="text-xs text-muted-foreground">Immediate attention</p>
            </CardContent>
          </Card>
        </HoverLift>
        <HoverLift>
          <Card className="animate-count-up card-lift kpi-card-green h-full">
            <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Resolved</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-field-green-subtle flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-field-green" />
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="text-2xl font-bold kpi-value text-field-green">
                {isLoading ? "—" : <CountUp value={resolvedCount} />}
              </div>
              <p className="text-xs text-muted-foreground">Closed items</p>
            </CardContent>
          </Card>
        </HoverLift>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Items</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="md:hidden space-y-3">
            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full rounded-md" />)
            ) : filtered.length > 0 ? (
              filtered.map((m) => (
                <div key={m.id} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
                        <span>{m.title}</span>
                        {m.isSample && <SampleBadge />}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{m.description}</div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border ${SEVERITY_STYLES[m.severity]}`}>
                      {m.severity}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={`inline-flex items-center font-medium px-2 py-1 rounded-full border ${STATUS_STYLES[m.status]}`}>
                      {m.status.replace("_", " ")}
                    </span>
                    <span className="text-muted-foreground">{TYPE_LABELS[m.type] ?? m.type}</span>
                    {m.coachName && (
                      <span className="text-muted-foreground">· {m.coachName} ({m.sport})</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <div>Due: {m.dueDate ? format(new Date(m.dueDate), "MMM d, yyyy") : "—"}</div>
                    <div>Assigned: {m.assignedTo ?? "Unassigned"}</div>
                  </div>
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {m.status === "open" && (
                        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => handleStatusChange(m.id, "in_progress")}>Start</Button>
                      )}
                      {m.status === "in_progress" && (
                        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => handleStatusChange(m.id, "pending_review")}>Review</Button>
                      )}
                      {(m.status === "open" || m.status === "in_progress" || m.status === "pending_review") && (
                        <Button size="sm" className="h-9 text-xs bg-green-600 hover:bg-green-700" onClick={() => setResolveDialog(m.id)}>Resolve</Button>
                      )}
                      {m.status === "open" && (
                        <Button size="sm" variant="ghost" className="h-9 text-xs text-muted-foreground" onClick={() => handleStatusChange(m.id, "dismissed")}>Dismiss</Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              statusFilter === "active" && severityFilter === "all" ? (
                <EmptyState
                  icon={Users}
                  title="No open risk items"
                  description="Risk items are typically generated from coach contracts and insurance policies. Add coaches and record their insurance coverage to start tracking compliance risks — or log a general risk item above."
                  action={isAdmin ? { label: "Add a coach", href: "/coaches/new" } : undefined}
                  secondaryAction={{ label: "View insurance", href: "/insurance" }}
                />
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground italic">No risk items match the current filters.</div>
              )
            )}
          </div>
          <div className="hidden md:block rounded-md border overflow-x-auto">
            <Table className="min-w-[900px] table-refined">
              <TableHeader>
                <TableRow>
                  <TableHead>Risk Item</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Coach / Program</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  [1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length > 0 ? (
                  filtered.map((m) => (
                    <TableRow key={m.id} className="align-top">
                      <TableCell className="max-w-[200px]">
                        <div className="font-medium text-sm leading-tight flex items-center gap-1.5 flex-wrap">
                          <span>{m.title}</span>
                          {m.isSample && <SampleBadge />}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{m.description}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{TYPE_LABELS[m.type] ?? m.type}</span>
                      </TableCell>
                      <TableCell>
                        {m.coachName ? (
                          <div>
                            <div className="text-sm font-medium">{m.coachName}</div>
                            <div className="text-xs text-muted-foreground">{m.sport}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">General</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border ${SEVERITY_STYLES[m.severity]}`}>
                          {m.severity}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border ${STATUS_STYLES[m.status]}`}>
                          {m.status.replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell>
                        {m.dueDate ? (
                          <span className="text-xs text-muted-foreground">{format(new Date(m.dueDate), "MMM d, yyyy")}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">{m.assignedTo ?? "Unassigned"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin ? (
                          <div className="flex flex-col gap-1 items-end">
                            {m.status === "open" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusChange(m.id, "in_progress")}>
                                Start
                              </Button>
                            )}
                            {m.status === "in_progress" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusChange(m.id, "pending_review")}>
                                Review
                              </Button>
                            )}
                            {(m.status === "open" || m.status === "in_progress" || m.status === "pending_review") && (
                              <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => setResolveDialog(m.id)}>
                                Resolve
                              </Button>
                            )}
                            {(m.status === "open") && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => handleStatusChange(m.id, "dismissed")}>
                                Dismiss
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">View only</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="p-0">
                      {statusFilter === "active" && severityFilter === "all" ? (
                        <EmptyState
                          icon={Users}
                          title="No open risk items"
                          description="Risk items are typically generated from coach contracts and insurance policies. Add coaches and record their insurance coverage to start tracking compliance risks — or log a general risk item above."
                          action={isAdmin ? { label: "Add a coach", href: "/coaches/new" } : undefined}
                          secondaryAction={{ label: "View insurance", href: "/insurance" }}
                        />
                      ) : statusFilter === "resolved" ? (
                        <EmptyState
                          icon={CheckCircle2}
                          title="No resolved items yet"
                          description="Resolved risk items will appear here once you close them out."
                        />
                      ) : (
                        <EmptyState
                          icon={AlertTriangle}
                          title="No items match your filters"
                          description="Try adjusting the status or severity filters to see more results."
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log New Risk Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity *</Label>
                <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input className="mt-1" placeholder="Brief description of the risk" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea className="mt-1" placeholder="Detailed description of the risk and impact..." value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <Label>Mitigation Plan</Label>
              <Textarea className="mt-1" placeholder="Steps planned to address this risk..." value={form.mitigationPlan} onChange={(e) => setForm((f) => ({ ...f, mitigationPlan: e.target.value }))} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Coach (optional)</Label>
                <Select value={form.coachId} onValueChange={(v) => setForm((f) => ({ ...f, coachId: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {coaches?.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.firstName} {c.lastName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" className="mt-1" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Assigned To</Label>
              <Input className="mt-1" placeholder="e.g. Compliance Office" value={form.assignedTo} onChange={(e) => setForm((f) => ({ ...f, assignedTo: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); setForm({ ...defaultForm }); }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMitigation.isPending}>
              {createMitigation.isPending ? "Saving..." : "Log Risk Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resolveDialog !== null} onOpenChange={() => setResolveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Risk Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Resolution Notes</Label>
            <Textarea placeholder="Describe how this risk was resolved..." value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => resolveDialog && handleResolve(resolveDialog)} disabled={updateMitigation.isPending}>
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
